import Links from '@/shared/constants/navegacao';
import ModalExcluir from '@/shared/components/modals/ModalExcluir';
import { CandidatoService } from './candidato.service';
import { CursoService } from '@/modules/structure/curso/curso.service';
import { InstituicaoEnsinoService } from '@/modules/structure/instituicoes/instituicao.service';
import { Column, DataTable } from '@/shared/components/tables/DataTable';
import { DataEHoraAmigavel } from '@/shared/utils/datas';
import { Link, useParams } from 'react-router-dom';
import { ProcessoSeletivoService } from '../processo-seletivo/processo-seletivo.service';
import { ReadCandidatoDto } from '@/api.types';
import { formatarNomePessoa } from '@/shared/utils/textos';
import { toast } from 'react-toastify';
import { useBreadcrumbActions } from '@/shared/contexts/BreadcrumbActionsContext';
import { useDataTableState } from '@/shared/components/tables/useDataTableState';
import { useEffect, useState } from 'react';
import { Button } from 'react-bootstrap';
import { FaEye, FaRegFilePdf } from 'react-icons/fa';
import { exportFromJsxToPdf } from '@/shared/utils/pdf';
import { CandidatoDetalhesPdf } from './presentation/components/CandidatoDetalhesPDF';
import {
  formatarCpfExibicao,
  formatarRacaCandidato,
  formatarSituacaoCandidato,
} from './candidatoDetalhes.utils';

const candidatoService = new CandidatoService();
const processoSeletivoService = new ProcessoSeletivoService();
const cursoService = new CursoService();
const instituicaoEnsinoService = new InstituicaoEnsinoService();

export default function CandidatoIndexPage() {
  const { setActions } = useBreadcrumbActions();

  const params = useParams();
  const processoSeletivoId = params.id ? Number(params.id) : undefined;

  const [registros, setRegistros] = useState<[ReadCandidatoDto[], number]>();
  const [processoSeletivo, setProcessoSeletivo] = useState<{ nome: string } | null>(null);
  const [modalExcluir, setModalExcluir] = useState<{ id: number; nome: string } | null>(null);
  const [printingCandidatoId, setPrintingCandidatoId] = useState<number | null>(null);
  const tableState = useDataTableState('dataInscricao');

  useEffect(() => {
    if (processoSeletivoId) {
      processoSeletivoService
        .findOne(processoSeletivoId)
        .then((result) => {
          setProcessoSeletivo({ nome: result.nome });
        })
        .catch((error) => {
          console.error('Falha ao carregar processo seletivo:', error);
        });
    }
  }, [processoSeletivoId]);

  useEffect(() => {
    if (!processoSeletivoId)
      return;

    setActions({
      secondary: {
        label: 'Gestão de Arquivos',
        href: `${Links.Candidato_Gestao_Arquivos}/${processoSeletivoId}`,
        onClick: () => { },
      },
    });
    return () => setActions({});
  }, [processoSeletivoId, setActions]);

  const handleBuscar = () => {
    candidatoService.findAll({
      skip: (tableState.currentPage - 1) * tableState.pageSize,
      take: tableState.pageSize,
      where: tableState.filter || undefined,
      orderBy: `${tableState.sortConfig.key} ${tableState.sortConfig.direction}`,
    }, processoSeletivoId).then((result) => {
      setRegistros(result);
    }).catch((res) => {
      toast.error(String(res) || '');
    });
  };

  useEffect(() => {
    if (processoSeletivoId) {
      handleBuscar();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [processoSeletivoId]);

  const handleConfirmarExcluir = async () => {
    if (!modalExcluir) return;
    try {
      await candidatoService.delete(modalExcluir.id);
      toast.success('Candidato excluído.');
      setModalExcluir(null);
      handleBuscar();
    } catch (error) {
      toast.error(String(error));
    }
  };

  const handlePrintCandidato = async (candidatoId: number) => {
    if (printingCandidatoId === candidatoId) return;
    setPrintingCandidatoId(candidatoId);
    try {
      const candidato = await candidatoService.findOne(candidatoId);

      const processo = candidato.processoSeletivoId
        ? await processoSeletivoService
          .findOne(candidato.processoSeletivoId)
          .then((ps) => ({ nome: ps.nome, provaUrl: ps.provaUrl }))
          .catch(() => null)
        : null;

      const cursoPretendidoNomeExtra =
        candidato.cursoPretendidoId && !candidato.cursoPretendidoNome?.trim()
          ? await cursoService
            .findOne(candidato.cursoPretendidoId)
            .then((c) => c.nome)
            .catch(() => null)
          : null;

      const instituicaoRazaoSocialExtra =
        candidato.instituicaoEnsinoId && !candidato.instituicaoEnsinoNome?.trim()
          ? await instituicaoEnsinoService
            .Read(candidato.instituicaoEnsinoId)
            .then((inst) => inst?.razao_social ?? null)
            .catch(() => null)
          : null;

      exportFromJsxToPdf(
        <CandidatoDetalhesPdf
          candidato={candidato}
          processoSeletivo={processo}
          cursoPretendidoNomeExtra={cursoPretendidoNomeExtra}
          instituicaoRazaoSocialExtra={instituicaoRazaoSocialExtra}
        />,
      );
    } catch (error) {
      toast.error(String(error));
    } finally {
      setPrintingCandidatoId((current) => (current === candidatoId ? null : current));
    }
  };

  const formatarSimNao = (valor?: boolean) => {
    if (valor === true) return 'sim';
    if (valor === false) return 'não';
    return '-';
  };

  const formatarPcd = (pessoaComDeficiencia?: boolean, optaVagasPcd?: boolean) => {
    if (optaVagasPcd === true) return 'PCD (concorrendo às vagas)';
    if (pessoaComDeficiencia === true) return 'PCD';
    if (pessoaComDeficiencia === false || optaVagasPcd === false) return 'Não';
    return '-';
  };

  const colunas: Column<ReadCandidatoDto>[] = [
    {
      key: "nomeCandidato",
      header: "Nome",
      render: (row) =>
        row.situacao === 'rascunho' && !row.pessoa?.nome
          ? 'Rascunho'
          : formatarNomePessoa(row.pessoa?.nome) || '—',
    },
    {
      key: "cpfCandidato",
      header: "CPF",
      invisible: true,
      render: (row) => formatarCpfExibicao(row.pessoa?.cpf),
    },
    {
      key: "emailCandidato",
      header: "E-mail",
      invisible: true,
      render: (row) => row.pessoa?.email || '-',
    },
    {
      key: "telefoneCandidato",
      header: "Telefone",
      invisible: true,
      render: (row) => row.pessoa?.telefoneFormatado || '-',
    },
    {
      key: "municipio",
      header: "Município",
      render: (row) => row.municipio || '-',
    },
    {
      key: "raca",
      header: "Identificação Racial",
      render: (row) => formatarRacaCandidato(row.raca),
    },
    {
      key: "optaVagasNegros",
      header: "Opta vagas pessoas negras",
      render: (row) => formatarSimNao(row.optaVagasNegros),
    },
    {
      key: "pessoaComDeficiencia",
      header: "PCD",
      render: (row) => formatarPcd(row.pessoaComDeficiencia, row.optaVagasPcd),
    },
    {
      key: "optaVagasPcd",
      header: "Opta vagas PCD",
      render: (row) => formatarSimNao(row.optaVagasPcd),
    },
    {
      key: "cursoPretendidoNome",
      header: "Curso",
      render: (row) => row.cursoPretendidoNome || '-',
    },
    {
      key: "dataInscricao",
      header: "Data de Inscrição",
      render: (row) => DataEHoraAmigavel(row.dataInscricao) || '-',
    },
    {
      key: "situacao",
      header: "Situação",
      render: (row) => formatarSituacaoCandidato(row.situacao),
    },
    {
      key: "acoes",
      header: "Ações",
      render: (row) => (
        <div className="d-flex align-items-center">
          <Link className="link-secondary me-3" to={`${Links.Candidato_Detalhes}/${row.id}`} title="Ver detalhes do candidato.">
            <FaEye size={24} color="#0daaaf" />
          </Link>
          <Button
            className="btn btn-link link-secondary p-0"
            onClick={() => handlePrintCandidato(row.id)}
            title="Gerar PDF dos detalhes do candidato."
            disabled={printingCandidatoId === row.id}
          >
            <FaRegFilePdf size={22} color="#ff4444" />
          </Button>
          {/* <Button
            className="btn btn-link link-secondary p-0 ms-3"
            onClick={() =>
              setModalExcluir({
                id: row.id,
                nome: formatarNomePessoa(row.pessoa?.nome) || 'Candidato',
              })
            }
            title="Excluir candidato (teste)"
          >
            <FaTrash size={20} color="#dc3545" />
          </Button> */}
        </div>
      ),
    },
  ];

  return (
    <div className="row">
      <div className="col-md-8">
        <h3>
          Candidatos
          {processoSeletivo && ` - ${processoSeletivo.nome}`}
        </h3>
        <span className="blockquote-footer pb-4 text-end">
          Listagem de candidatos inscritos no processo seletivo.
        </span>
      </div>
      <hr className="pb-4" />

      {registros && (
        <DataTable
          columns={colunas}
          data={registros[0]}
          initialSortKey="dataInscricao"
          onChange={handleBuscar}
          serverSide={true}
          states={tableState}
          totalRecords={registros[1]}
        />
      )}

      <ModalExcluir
        show={modalExcluir !== null}
        handleExcluir={() => void handleConfirmarExcluir()}
        handleHide={() => setModalExcluir(null)}
        mensagemPersonalizada={
          modalExcluir
            ? `Tem certeza que deseja excluir o candidato "${modalExcluir.nome}"? Esta ação não pode ser desfeita.`
            : undefined
        }
      />
    </div>
  );
}

